/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, NgZone, OnInit} from '@angular/core';
import * as _ from 'lodash';
import {Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import template from './third-party-audience-upload-modal.component.html';
import {ThirdPartyAudienceResource} from '../../components/api/resources/resource/third-party-audience.resource';

const DEFAULT_PROVIDER = 'LC_MEDIA';

interface UploadModalConfig {
  title: string;
  instructions: string;
  columns: string[];
  examples: string[];
}

const UPLOAD_MODAL_CONFIG: Record<'data' | 'definitions', UploadModalConfig> = {
  data: {
    title: 'Upload Audience Data',
    instructions: 'Upload a CSV file that assigns postcode data to existing third-party audiences. The first column is the audience reference code, and a variable number of columns are used for the postcodes',
    columns: ['Audience external reference', 'Postcode 1', 'Postcode 2', '...', 'Postcode N'],
    examples: [
      'audience_ref_123,0001,0430,0124',
      'audience_ref_456,0005',
      'audience_ref_789,0099,0657,006,0700'
    ]
  },
  definitions: {
    title: 'Batch Upload Audience Definitions',
    instructions: 'Upload a CSV file that creates or updates third-party audience definitions.',
    columns: ['Audience external reference', 'Audience name', 'Description', 'Teams (optional)'],
    examples: [
      'audience_ref_123,High Income,People that live in high income houses,team_abc',
      'audience_ref_456,Medium Income,People that live in medium income houses,',
      'audience_ref_789,Low Income,People that live in low income houses,team_xyz,team_abc'
    ]
  }
};

@Component({
  standalone: false,
  selector: 'adn-third-party-audience-upload-modal',
  template
})
export class ThirdPartyAudienceUploadModalComponent implements OnInit {
  @Input() uploadType: 'data' | 'definitions' = 'definitions';

  readonly onClosed = new Subject<any>();

  readonly providers = ['NORDIC_DATA_RESOURCES', 'LC_MEDIA'];

  title = '';
  instructions = '';
  columns: string[] = [];
  examplesText = '';

  uploadParams: {provider: string; countryIsoCode?: string} = {
    provider: DEFAULT_PROVIDER
  };

  progress = 0;
  validationErrors: any[] = [];
  uploadError: string | null = null;
  result: any = null;
  uploadedCount = 0;
  receivedCount = 0;
  fileName = '';
  uploading = false;

  constructor(
    public bsModalRef: BsModalRef,
    @Inject(ThirdPartyAudienceResource) private readonly ThirdPartyAudience: any,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    const config = UPLOAD_MODAL_CONFIG[this.uploadType];
    this.title = config.title;
    this.instructions = config.instructions;
    this.columns = config.columns;
    this.examplesText = config.examples.join('\n');

    if (this.isDataUpload) {
      this.uploadParams.countryIsoCode = 'NO';
    }
  }

  get isDataUpload(): boolean {
    return this.uploadType === 'data';
  }

  canUpload(): boolean {
    return !!this.uploadParams.provider && (!this.isDataUpload || !!this.uploadParams.countryIsoCode);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.canUpload()) {
      return;
    }
    this.upload(file);
  }

  upload(file: File): void {
    this.fileName = file.name;
    this.progress = 0;
    this.result = null;
    this.uploadError = null;
    this.validationErrors = [];
    this.uploading = true;
    this.cdr.markForCheck();

    const onProgress = (progressEvent: any) => {
      if (progressEvent?.total) {
        this.ngZone.run(() => {
          this.progress = Math.min(100, parseInt(String(100 * progressEvent.loaded / progressEvent.total), 10));
          this.cdr.markForCheck();
        });
      }
    };
    const uploadPromise = this.isDataUpload ?
      this.ThirdPartyAudience.uploadAudienceData({file}, this.uploadParams, onProgress) :
      this.ThirdPartyAudience.uploadAudienceDefinitions({file}, {
        provider: this.uploadParams.provider
      }, onProgress);

    uploadPromise.then((response: any) => {
      this.ngZone.run(() => {
        this.result = _.get(response, ['data'], response);
        this.uploadedCount = _.get(this.result, ['parameters', 'uploaded'], 0);
        this.receivedCount = _.get(this.result, ['parameters', 'received'], 0);
        this.progress = 100;
        this.cdr.markForCheck();
      });
    }, (error: any) => {
      this.ngZone.run(() => {
        this.validationErrors = _.get(error, ['data', 'errors'], []);
        if (!this.validationErrors.length) {
          this.uploadError = _.get(error, ['data', 'message']) || 'The file could not be uploaded.';
        }
        this.cdr.markForCheck();
      });
    }).finally(() => {
      this.ngZone.run(() => {
        this.uploading = false;
        this.cdr.markForCheck();
      });
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
    this.onClosed.next(this.result);
  }
}
